import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { acquisitionsApi, type VendorDetail } from "@/lib/api/acquisitions";
import { VendorSummaryStrip } from "./VendorSummaryStrip";
import { VendorContactSection } from "./VendorContactSection";
import { VendorTermsSection } from "./VendorTermsSection";
import { VendorRecentOrders } from "./VendorRecentOrders";
import { Loader2, Pencil } from "lucide-react";
import { useToast } from "@/contexts/ToastContext";

export default function VendorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [vendor, setVendor] = useState<VendorDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    acquisitionsApi.vendors
      .getById(id)
      .then(setVendor)
      .catch(() => {
        toast.error("Failed to load vendor.");
        setVendor(null);
      })
      .finally(() => setLoading(false));
  }, [id, toast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!vendor) {
    return <div className="py-20 text-center text-slate-500">Vendor not found</div>;
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title={vendor.name}
        description={`Vendor code ${vendor.code}`}
        breadcrumbItems={[
          { label: "Vendors", href: "/acquisitions/vendors" },
          { label: vendor.name },
        ]}
        showSearch={false}
        titleSize="md"
        actions={
          <Button onClick={() => navigate(`/acquisitions/vendors/${vendor.id}/edit`)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </Button>
        }
      />

      <div className="space-y-5">
        <VendorSummaryStrip vendor={vendor} />
        <VendorContactSection vendor={vendor} />
        <VendorTermsSection vendor={vendor} />
        <VendorRecentOrders orders={vendor.purchaseOrders} />
        {vendor.notes ? (
          <section className="rounded-lg border border-slate-200 bg-white px-4 py-4 sm:px-5">
            <h2 className="text-sm font-semibold text-slate-900">Notes</h2>
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600">{vendor.notes}</p>
          </section>
        ) : null}
      </div>
    </PageContainer>
  );
}
